/**
 * Module dependencies.
 */

import { FormList } from 'app/components/organisms/crud/form-types';
import { exclusiveRule, requiredRule } from 'app/core/utils/field-rules';
import { usePaginatedCrudRequest } from 'app/hooks/app-requests/crud/use-paginated-crud-request';
import { User } from 'app/types/user';
import { TFunction } from 'react-i18next';
import React from 'react';
import { RenderUserItem } from './render-user-item';

/**
 * Left.
 */

const left = (translate: TFunction, type: 'add' | 'edit'): FormList => [
  {
    type: 'selectField',
    name: 'userId',
    disabled: type === 'edit',
    showSearch: true,
    allowClear: true,
    rules: exclusiveRule(translate, { field: 'phoneNumber', label: translate('common.labels.phoneNumber') }, true),
    label: translate('common.labels.user'),
    paginated: {
      enabled: true,
      pageSize: 50
    },
    options: {
      hook: usePaginatedCrudRequest as any,
      hookProps: (_values: any, _params: any, options: any) => {
        const searchValue = options?.searchValue?.trim();

        return [
          {
            key: ['users-paginated', searchValue || ''],
            endpoint: 'users' as any,
            options: {
              ...(searchValue ? { search: searchValue } : {})
            },
            pageSize: 50
          }
        ];
      },
      normalize: (items: User[]) => {
        if (!items || !Array.isArray(items)) {
          return [];
        }

        return items
          .filter(item => item && item.id && item.phoneNumber)
          .map(item => ({
            value: item.id,
            label: <RenderUserItem item={item} />,
            searchValue: item.name
          }));
      }
    }
  },
  {
    type: 'phoneNumberField',
    name: 'phoneNumber',
    disabled: type === 'edit',
    rules: exclusiveRule(translate, { field: 'userId', label: translate('common.labels.user') }, true),
    label: translate('common.labels.phoneNumber')
  },
  {
    type: 'selectField',
    name: 'pharmacyId',
    disabled: type === 'edit',
    showSearch: true,
    allowClear: true,
    rules: requiredRule(translate),
    label: translate('common.labels.pharmacy'),
    paginated: {
      enabled: true,
      pageSize: 50
    },
    options: {
      hook: usePaginatedCrudRequest as any,
      hookProps: (_values: any, _params: any, options: any) => {
        const searchValue = options?.searchValue?.trim();

        return [
          {
            key: ['pharmacies-paginated', searchValue || ''],
            endpoint: 'pharmacies' as any,
            options: {
              ...(searchValue ? { search: searchValue } : {})
            },
            pageSize: 50
          }
        ];
      },
      normalize: (items: User[]) => {
        if (!items || !Array.isArray(items)) {
          return [];
        }

        return items
          .filter(item => item && item.id)
          .map(item => ({
            value: item.id,
            label: item.name,
            searchValue: item.name
          }));
      }
    }
  },
  {
    type: 'selectField',
    name: 'role',
    label: translate('roles.labels.role'),
    rules: requiredRule(translate),
    options: [
      {
        value: 'owner',
        label: String(translate('roles.labels.statusLabels.owner'))
      },
      {
        value: 'operator',
        label: String(translate('roles.labels.statusLabels.operator'))
      },
      {
        value: 'viewer',
        label: String(translate('roles.labels.statusLabels.viewer'))
      }
    ]
  },
  {
    type: 'checkBoxField',
    name: 'confirmed',
    label: translate('common.labels.confirmed'),
    rules: undefined
  },
  {
    type: 'checkBoxField',
    name: 'blocked',
    label: translate('common.labels.blocked'),
    rules: undefined
  }
];

/**
 * Right.
 */

const right = (translate: TFunction): FormList => [
  {
    type: 'inputField',
    name: 'id',
    disabled: true,
    label: translate('common.labels.id'),
    rules: undefined
  },
  {
    type: 'inputField',
    name: 'createdAt',
    disabled: true,
    label: translate('common.labels.createdAt'),
    rules: undefined
  },
  {
    type: 'inputField',
    name: 'updatedAt',
    disabled: true,
    label: translate('common.labels.updatedAt'),
    rules: undefined
  }
];

/**
 * Export `formFields`.
 */

export const formFields = (translate: TFunction, type: 'add' | 'edit'): FormList => [
  {
    type: 'columns',
    gutter: 40,
    name: 'mainWire',
    columns: [
      {
        column: 18,
        children: left(translate, type)
      },
      {
        column: 6,
        children: right(translate)
      }
    ]
  }
];
